import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { categoryService } from '../../../services/categoryService';

const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryParentId, setEditCategoryParentId] = useState('');
  const [loading, setLoading] = useState(true);

  // --- Helpers ---

  const flattenCategories = (cats, level = 0) => {
    let flattened = [];
    cats.forEach(category => {
      flattened.push({
        ...category,
        displayName: '  '.repeat(level) + (level > 0 ? '└─ ' : '') + category.name,
        level
      });
      if (category.children && category.children.length > 0) {
        flattened = flattened.concat(flattenCategories(category.children, level + 1));
      }
    });
    return flattened;
  };

  // --- CRUD ---

  const loadCategories = useCallback(async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data || []);
    } catch (err) {
      console.error('Błąd podczas pobierania kategorii:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadCategories();
      setLoading(false);
    };
    init();
  }, [loadCategories]);

  const addCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Wprowadź nazwę kategorii');
      return;
    }
    try {
      const categoryData = {
        name: newCategoryName.trim(),
        parent_id: newCategoryParentId || null
      };
      await categoryService.createCategory(categoryData);
      setNewCategoryName('');
      setNewCategoryParentId('');
      loadCategories();
      toast.success('Kategoria została dodana pomyślnie');
    } catch (err) {
      console.error('Błąd podczas dodawania kategorii:', err);
      toast.error('Błąd podczas dodawania kategorii');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę kategorię?')) return;
    try {
      await categoryService.deleteCategory(categoryId);
      loadCategories();
      toast.success('Kategoria została usunięta pomyślnie');
    } catch (err) {
      console.error('Błąd podczas usuwania kategorii:', err);
      toast.error('Błąd podczas usuwania kategorii');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category.id);
    setEditCategoryName(category.name);
    setEditCategoryParentId(category.parent_id || '');
  };

  const cancelEditCategory = () => {
    setEditingCategory(null);
    setEditCategoryName('');
    setEditCategoryParentId('');
  };

  const updateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) {
      toast.error('Wprowadź nazwę kategorii');
      return;
    }
    try {
      const categoryData = {
        name: editCategoryName.trim(),
        parent_id: editCategoryParentId || null
      };
      await categoryService.updateCategory(categoryId, categoryData);
      setEditingCategory(null);
      setEditCategoryName('');
      setEditCategoryParentId('');
      loadCategories();
      toast.success('Kategoria została zaktualizowana pomyślnie');
    } catch (err) {
      console.error('Błąd podczas aktualizacji kategorii:', err);
      toast.error('Błąd podczas aktualizacji kategorii');
    }
  };

  // --- CategoryTree (defined inside to access editing state) ---

  const CategoryTree = ({ categories: treeCats, level = 0 }) => {
    return treeCats.map(category => (
      <div key={category.id}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: `${level * 20}px`,
          padding: '0.5rem',
          backgroundColor: level % 2 === 0 ? 'var(--bg)' : 'var(--card-bg)' ,
          borderBottom: '1px solid var(--border)'
        }}>
          {level > 0 && (
            <span style={{ marginRight: '0.5rem', color: 'var(--text-muted)' }}>
              {'└─'.repeat(Math.min(level, 1))}
            </span>
          )}
          <span style={{
            fontSize: '0.875rem',
            fontWeight: level === 0 ? '600' : '400',
            color: level === 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
            flex: 1
          }}>
            {level === 0 ? '📁' : '📄'} {category.name}
          </span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {editingCategory === category.id ? (
              <>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer' }}
                  onClick={() => updateCategory(category.id)}
                >&#10003;</button>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , cursor: 'pointer' }}
                  onClick={cancelEditCategory}
                >&#10005;</button>
              </>
            ) : (
              <>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' , cursor: 'pointer' }}
                  onClick={() => startEditCategory(category)}
                >&#9999;&#65039;</button>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' , cursor: 'pointer' }}
                  onClick={() => deleteCategory(category.id)}
                >&#128465;&#65039;</button>
              </>
            )}
          </div>
        </div>
        {editingCategory === category.id && (
          <div style={{
            marginLeft: `${level * 20}px`,
            padding: '0.5rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                style={{ flex: '1', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', outline: 'none' }}
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') updateCategory(category.id); }}
              />
            </div>
            <select
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', outline: 'none' }}
              value={editCategoryParentId}
              onChange={(e) => setEditCategoryParentId(e.target.value)}
            >
              <option value="">-- Kategoria główna --</option>
              {flattenCategories(categories)
                .filter(cat => cat.id !== category.id)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.displayName}</option>
                ))}
            </select>
          </div>
        )}
        {category.children && category.children.length > 0 && (
          <CategoryTree categories={category.children} level={level + 1} />
        )}
      </div>
    ));
  };

  // --- Render ---

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Ładowanie kategorii...</div>
      </div>
    );
  }

  return (
    <div className="admin-table-wrap" style={{ maxWidth: '800px' }}>
      <style>{`
        @media (max-width: 600px) {
          .cat-form-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 601px) and (max-width: 900px) {
          .cat-form-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
      {/* Formularz dodawania */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '5px',
        marginBottom: '1rem',
        padding: '0.75rem',
        background: 'var(--bg)'
      }}>
        <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
          &#10133; Nowa Kategoria
        </h6>
        <div className="cat-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Nazwa *
            </label>
            <input
              type="text"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', height: '28px' }}
              placeholder="Nazwa kategorii"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCategory()}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Kategoria nadrzędna
            </label>
            <select
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', height: '28px' }}
              value={newCategoryParentId}
              onChange={(e) => setNewCategoryParentId(e.target.value)}
            >
              <option value="">-- Główna --</option>
              {flattenCategories(categories).map(cat => (
                <option key={cat.id} value={cat.id}>{cat.displayName}</option>
              ))}
            </select>
          </div>
          <button
            style={{ padding: '4px 10px', border: '1px solid var(--success)', color: 'var(--success)' , fontSize: '11px', fontWeight: '500', cursor: 'pointer', height: '28px' }}
            onClick={addCategory}
            disabled={!newCategoryName.trim()}
          >
            Dodaj
          </button>
        </div>
      </div>

      {/* Drzewo kategorii */}
      <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ backgroundColor: 'var(--border)', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1rem' }}>&var(--cyan);&#65039;</span>
            <h6 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>Struktura Kategorii</h6>
          </div>
          <span style={{ fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' , padding: '2px 8px', borderRadius: '10px' }}>{categories.length}</span>
        </div>
        <div style={{ maxHeight: '450px', overflowY: 'auto', padding: '0.5rem' }}>
          {categories.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', margin: 0, padding: '1rem', fontSize: '12px' }}>Brak kategorii</p>
          ) : (
            <CategoryTree categories={categories} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesTab;
